import type { RngState } from "./types.ts";

function normalizeSeed(seed: number | string) {
  if (typeof seed === "number") return (seed >>> 0) || 0x6d2b79f5;
  let hash = 2166136261;
  for (const character of seed) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) || 0x6d2b79f5;
}

export function createRng(seed: number | string): RngState {
  const normalized = normalizeSeed(seed);
  return { seed: normalized, state: normalized, calls: 0 };
}

export function nextRng(rng: RngState): { value: number; rng: RngState } {
  const state = (rng.state + 0x6d2b79f5) >>> 0;
  let value = state;
  value = Math.imul(value ^ (value >>> 15), value | 1);
  value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
  value = ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  return { value, rng: { ...rng, state, calls: rng.calls + 1 } };
}

export function randomInt(rng: RngState, min: number, max: number) {
  const next = nextRng(rng);
  return { value: Math.floor(next.value * (max - min + 1)) + min, rng: next.rng };
}

export function pickWeighted<T>(rng: RngState, items: T[], weight: (item: T) => number) {
  const total = items.reduce((sum, item) => sum + Math.max(0, weight(item)), 0);
  if (!items.length || total <= 0) return { value: undefined, rng };
  const next = nextRng(rng);
  let roll = next.value * total;
  for (const item of items) {
    roll -= Math.max(0, weight(item));
    if (roll <= 0) return { value: item, rng: next.rng };
  }
  return { value: items.at(-1), rng: next.rng };
}
